// components/ImageUpload.tsx
import React, { useRef, useState } from 'react'
import { uploadImage, getImageUrl } from '../services/productRequestApi'

interface ImageUploadProps {
  onImageUploaded: (imageId: string) => void
  onError?: (error: string) => void
  maxSizeMB?: number
  acceptedTypes?: string[]
  className?: string
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUploaded,
  onError,
  maxSizeMB = 10,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploadedImageId, setUploadedImageId] = useState<string | null>(null)

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload JPEG, PNG, or WebP images.'
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      return `File size exceeds ${maxSizeMB}MB limit.`
    }

    return null
  }

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file
    const validationError = validateFile(file)
    if (validationError) {
      onError?.(validationError)
      return
    }

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string)
    }
    reader.readAsDataURL(file)

    // Upload file
    setUploading(true)
    try {
      const imageId = await uploadImage(file)
      setUploadedImageId(imageId)
      onImageUploaded(imageId)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      onError?.(errorMessage)
      setPreviewUrl(null)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = () => {
    setPreviewUrl(null)
    setUploadedImageId(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onImageUploaded('') // Clear the image ID
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* File Input */}
      <div>
        <input
          ref={fileInputRef}
          type='file'
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className='hidden'
          id='image-upload'
          disabled={uploading}
        />
        <label
          htmlFor='image-upload'
          className={`
            flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer
            transition-colors duration-200
            ${
              uploading
                ? 'cursor-not-allowed opacity-60'
                : 'hover:border-blue-400'
            }
          `}
          style={{
            borderColor: uploading
              ? 'var(--border-color)'
              : 'var(--border-color)',
            backgroundColor: uploading ? 'var(--bg-secondary)' : 'transparent',
          }}
        >
          {uploading ? (
            <div className='flex flex-col items-center space-y-2'>
              <div
                className='animate-spin rounded-full h-8 w-8 border-b-2'
                style={{ borderColor: 'var(--accent-primary)' }}
              ></div>
              <span
                className='text-sm'
                style={{ color: 'var(--text-secondary)' }}
              >
                در حال آپلود...
              </span>
            </div>
          ) : (
            <div className='flex flex-col items-center space-y-2'>
              <svg
                className='w-8 h-8'
                style={{ color: 'var(--text-tertiary)' }}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                />
              </svg>
              <span
                className='text-sm'
                style={{ color: 'var(--text-secondary)' }}
              >
                کلیک کنید برای آپلود تصویر
              </span>
              <span
                className='text-xs'
                style={{ color: 'var(--text-tertiary)' }}
              >
                فرمت‌های JPEG، PNG، WebP تا {maxSizeMB} مگابایت
              </span>
            </div>
          )}
        </label>
      </div>

      {/* Preview */}
      {previewUrl && (
        <div className='relative'>
          <img
            src={previewUrl}
            alt='پیش‌نمایش'
            className='w-full h-48 object-cover rounded-lg border'
            style={{ borderColor: 'var(--border-color)' }}
          />
          {!uploading && (
            <button
              onClick={handleRemove}
              className='absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors'
              type='button'
              aria-label='حذف تصویر'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Success indicator */}
      {uploadedImageId && !uploading && (
        <div className='flex items-center space-x-2 space-x-reverse text-sm text-green-600'>
          <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
              clipRule='evenodd'
            />
          </svg>
          <span>تصویر با موفقیت آپلود شد</span>
        </div>
      )}
    </div>
  )
}
